import type { FetchContext } from 'ofetch'
import type { FetchClient } from '~/api/types'
import * as Sentry from '@sentry/vue'
import { useAuthStore } from '~/stores/authStore'
import { useMonitoring } from '~/composables/monitoring/useMonitoring'
import { useSentry } from '~/composables/monitoring/useSentry'
import { useSessionExpiry } from '~/composables/auth/useSessionExpiry'
import { useActiveSite } from '~/composables/useActiveSite'
import { getApiErrorDetail } from '~/utils/api-errors'

interface ExtendedFetchOptions {
  _sentrySpan?: Sentry.Span
  [key: string]: string | number | boolean | undefined | Sentry.Span | null
}

const SITE_HEADER_ERRORS: Record<number, string> = {
  400: 'invalid X-Site-Id header',
  403: "X-Site-Id is outside the caller's site reach"
}

const isSiteHeaderError = (response: FetchContext['response'], options: FetchContext['options']): boolean => {
  const expectedDetail = response?.status ? SITE_HEADER_ERRORS[response.status] : undefined
  if (!expectedDetail || !options.headers?.has?.('X-Site-Id')) {
    return false
  }

  const detail = getApiErrorDetail({ data: response?._data })
  return detail?.split('\n')[0].trim() === expectedDetail
}

const extractUrl = (request: string | { url?: string } | null | undefined): string => {
  if (typeof request === 'string') {
    return request
  }
  if (request && typeof request.url === 'string') {
    return request.url
  }
  return 'unknown'
}

export const useFetchClient = (): FetchClient => {
  const config = useRuntimeConfig().public
  const apiGatewayUrl = config.apiUrl
  const authStore = useAuthStore()
  const nuxtApp = useNuxtApp()
  const { trackAPISuccess, trackAPIError, handleOfflineRequest } = useMonitoring()
  const { addBreadcrumb } = useSentry()

  return $fetch.create({
    onRequest({ options, request }: FetchContext) {
      if (authStore.isAuthenticated) {
        options.headers.set('Authorization', `Bearer ${authStore.accessToken}`)
      }

      if (!request) {
        return
      }

      const requestUrl = extractUrl(request)
      if (authStore.isAuthenticated && authStore.activeLicence && requestUrl.includes(apiGatewayUrl)) {
        options.headers.set('X-Fundamentum-Licence-Id', authStore.activeLicence.id.toString())
      }

      if (authStore.isAuthenticated && authStore.activeSite && requestUrl.includes(apiGatewayUrl)) {
        options.headers.set('X-Site-Id', authStore.activeSite.id.toString())
      }

      if (authStore.sessionId && requestUrl.includes(apiGatewayUrl)) {
        options.headers.set('X-Session-Id', authStore.sessionId)
      }

      // Start Sentry span for this request
      const method = options.method || 'GET'
      const url = extractUrl(request)

      const opt = options as unknown as ExtendedFetchOptions

      const parsedUrl = new URL(url, location.origin)
      opt._sentrySpan = Sentry.startInactiveSpan({
        op: 'http.client',
        name: `${method} ${url}`,
        attributes: {
          'http.method': method,
          'http.url': url,
          'http.path': parsedUrl.pathname,
          'http.host': parsedUrl.host,
          'http.target': parsedUrl.pathname + parsedUrl.search,
          'http.request.body.size': options.body ? JSON.stringify(options.body).length : 0
        }
      })

      addBreadcrumb(`API Request: ${method} ${url}`, 'api', {
        method,
        url,
        hasAuth: String(!!authStore.isAuthenticated),
        licenceId: authStore.activeLicence?.id?.toString() ?? ''
      })
    },

    onResponse({ response, options }: FetchContext) {
      const span = (options as unknown as ExtendedFetchOptions)._sentrySpan

      if (span) {
        span.setAttributes({
          'http.status_code': response?.status,
          'http.response.body.size': response?._data ? JSON.stringify(response._data).length : 0
        })
        span.setStatus({ code: 1, message: 'ok' })
        span.end()
      }

      // Track successful API calls
      trackAPISuccess(options.method || 'GET', response?.status || 200)
    },

    onResponseError({ response, options, error, request }: FetchContext) {
      const span = (options as unknown as ExtendedFetchOptions)._sentrySpan

      if (span) {
        span.setAttributes({
          'http.status_code': response?.status || 0,
          'error.message': error?.message || 'Unknown error'
        })
        span.setStatus({ code: 2, message: 'internal_error' })
        span.end()
      }

      const url = extractUrl(request)

      // Capture error with context
      Sentry.withScope((scope) => {
        scope.setContext('api_error', {
          url,
          method: options.method,
          status: response?.status,
          statusText: response?.statusText,
          data: response?._data,
          hasAuth: !!authStore.isAuthenticated
        })

        scope.setTag('api.status', response?.status)
        scope.setTag('api.endpoint', url)

        if (response?.status && response.status >= 500) {
          scope.setLevel('error')
        } else if (response?.status && response.status >= 400) {
          scope.setLevel('warning')
        }

        Sentry.captureException(error || new Error(`API Error: ${response?.status}`))
      })

      // Track API errors
      trackAPIError(options.method || 'GET', response?.status || 0, error?.message)

      if (isSiteHeaderError(response, options)) {
        nuxtApp.runWithContext(() => useActiveSite().resetActiveSite())
        return
      }

      if (response?.status === 401 && !url.startsWith(config.oauthServerUrl)) {
        nuxtApp.runWithContext(() => useSessionExpiry().triggerSessionExpired({ silent: true }))
      }
    },

    onRequestError({ error, options, request }: FetchContext) {
      const span = (options as unknown as ExtendedFetchOptions)._sentrySpan
      const url = extractUrl(request)

      if (span) {
        span.setAttributes({
          'error.type': 'network',
          'error.message': error?.message || 'Network error'
        })
        span.setStatus({ code: 2, message: 'network_error' })
        span.end()
      }

      // Handle network errors specifically
      if (!navigator.onLine && !!error) {
        handleOfflineRequest(url, options.method || 'GET', error)
      } else {
        Sentry.captureException(error, {
          tags: {
            'error.type': 'network',
            'api.url': url
          }
        })
      }
    }
  })
}
